import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.#normalizeColorSwatches(target || this);
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.#filterColorMedia(newMediaGallery, this.#getSelectedColor(event.target));
        this.replaceWith(newMediaGallery);
        requestAnimationFrame(() => this.#normalizeColorSwatches(event.target.closest('.shopify-section, dialog')));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #normalizeColorSwatches(root) {
    const colorMap = {
      ivory: '#fffdf7',
      'blush pink': '#fcccf0',
      pink: '#fcccf0',
      'light pink': '#ffb6c1',
      'coral pink': '#f88379',
      'porcelain pink': '#f2d4d0',
      black: '#000000',
      white: '#ffffff',
      'white gray': '#e8e8e8',
      gray: '#808080',
      'dark gray': '#404040',
      'gray green': '#8fbc8f',
      'gray blue': '#6b8cae',
      'gray purple': '#7b6b8a',
      'berry red': '#e23131',
      red: '#e23131',
      'dark red': '#8b0000',
      'wild berry red': '#8b0000',
      'reddish brown': '#7b3f2a',
      fuchsia: '#ff00ff',
      apricot: '#fbceb1',
      orange: '#ff8c00',
      yellow: '#ffd700',
      'neon yellow': '#ccff00',
      'light yellow': '#fffacd',
      green: '#228b22',
      'dark green': '#1a4a2e',
      'light green': '#90ee90',
      'palm green': '#2e8b57',
      cyan: '#00bcd4',
      'light cyan': '#e0ffff',
      'light blue': '#e6f8ff',
      'steel blue': '#4682b4',
      blue: '#2f54c8',
      'slate blue': '#2f54c8',
      'deep sea blue': '#003366',
      'soft navy': '#1c2a52',
      'navy blue': '#1c2a52',
      purple: '#9d68d5',
      'dusty mauve': '#9d68d5',
      'light purple': '#d8b4fe',
      'dark purple': '#4b0082',
      brown: '#5a3519',
      'mocha brown': '#5a3519',
      'dark brown': '#3b1f0f',
      'light brown': '#c4956a',
      'light beige': '#f5f0e8',
      floral: '#ff69b4',
      mint: '#98ff98',
      'electric blue': '#0066ff',
      indigo: '#4b0082',
      'olive green': '#6b8e23',
      'peacock blue': '#005f6b',
      teal: '#008080',
      burgundy: '#800020',
      'light gray': '#d3d3d3',
    };

    const scope = root || document;
    const colorInputs = scope.querySelectorAll?.('input[data-option-name="Color"]') || [];
    for (const input of colorInputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const color = colorMap[input.value.trim().toLowerCase()];
      if (!color) continue;

      const label = input.closest('label');
      if (!label || label.querySelector('.swatch')) continue;

      const textSwatch = label.querySelector('.variant-option__swatch-text');
      textSwatch?.remove();

      const swatch = document.createElement('span');
      swatch.className = 'swatch swatch--unscaled';
      swatch.style.setProperty('--swatch-background', color);

      const hiddenLabel = document.createElement('span');
      hiddenLabel.className = 'visually-hidden';
      hiddenLabel.textContent = input.value;

      input.insertAdjacentElement('afterend', swatch);
      swatch.insertAdjacentElement('afterend', hiddenLabel);
    }
  }

  #getSelectedColor(source) {
    if (source instanceof HTMLInputElement && source.dataset.optionName === 'Color') return source.value;

    const root = source instanceof Element ? source.closest('.shopify-section, dialog') : this.closest('.shopify-section, dialog');
    const checkedColor = root?.querySelector('input[data-option-name="Color"]:checked');

    return checkedColor instanceof HTMLInputElement ? checkedColor.value : '';
  }

  #filterColorMedia(gallery, color) {
    const terms = this.#colorMediaTerms(color);
    if (!terms.length) return;

    const mediaItems = Array.from(
      gallery.querySelectorAll('.product-media-container, .dialog-thumbnails-list__thumbnail')
    );
    if (!mediaItems.length) return;

    const matchingItems = mediaItems.filter((item) => this.#mediaItemMatchesColor(item, terms));
    if (!matchingItems.length) return;

    for (const item of mediaItems) {
      const image = item.querySelector('img');
      if (image && !this.#mediaItemMatchesColor(item, terms)) item.remove();
    }
  }

  #mediaItemMatchesColor(item, terms) {
    const image = item.querySelector('img');
    const mediaText = `${image?.alt || ''} ${image?.currentSrc || ''} ${image?.src || ''} ${image?.srcset || ''}`.toLowerCase();

    return terms.some((term) => mediaText.includes(term));
  }

  #colorMediaTerms(color) {
    const normalizedColor = color.trim().toLowerCase();
    const colorTerms = {
      ivory: ['ivory'],
      'blush pink': ['blush-pink', 'blush pink', 'pink'],
      pink: ['blush-pink', 'blush pink', 'pink'],
      'light pink': ['light-pink', 'light pink', 'pink'],
      'coral pink': ['coral-pink', 'coral pink', 'coral'],
      'porcelain pink': ['porcelain-pink', 'porcelain pink', 'porcelain'],
      black: ['black'],
      white: ['white'],
      'white gray': ['white-gray', 'white gray'],
      gray: ['gray', 'grey'],
      'dark gray': ['dark-gray', 'dark gray'],
      'gray green': ['gray-green', 'gray green'],
      'gray blue': ['gray-blue', 'gray blue'],
      'gray purple': ['gray-purple', 'gray purple'],
      'berry red': ['berry-red', 'berry red', 'red'],
      red: ['berry-red', 'berry red', 'red'],
      'dark red': ['dark-red', 'dark red'],
      'wild berry red': ['wild-berry-red', 'wild berry red', 'berry red', 'red'],
      'reddish brown': ['reddish-brown', 'reddish brown'],
      fuchsia: ['fuchsia'],
      apricot: ['apricot'],
      orange: ['orange'],
      yellow: ['yellow'],
      'neon yellow': ['neon-yellow', 'neon yellow'],
      'light yellow': ['light-yellow', 'light yellow'],
      green: ['green'],
      'dark green': ['dark-green', 'dark green'],
      'light green': ['light-green', 'light green'],
      'palm green': ['palm-green', 'palm green'],
      cyan: ['cyan'],
      'light cyan': ['light-cyan', 'light cyan'],
      'light blue': ['light-blue', 'light blue'],
      'steel blue': ['steel-blue', 'steel blue'],
      blue: ['slate-blue', 'slate blue', 'blue'],
      'slate blue': ['slate-blue', 'slate blue'],
      'deep sea blue': ['deep-sea-blue', 'deep sea blue', 'deep blue'],
      'soft navy': ['soft-navy', 'soft navy', 'navy'],
      'navy blue': ['soft-navy', 'soft navy', 'navy'],
      purple: ['dusty-mauve', 'dusty mauve', 'mauve', 'purple'],
      'dusty mauve': ['dusty-mauve', 'dusty mauve', 'mauve', 'purple'],
      'light purple': ['light-purple', 'light purple', 'lavender'],
      'dark purple': ['dark-purple', 'dark purple'],
      brown: ['mocha-brown', 'mocha brown', 'brown'],
      'mocha brown': ['mocha-brown', 'mocha brown', 'brown'],
      'dark brown': ['dark-brown', 'dark brown'],
      'light brown': ['light-brown', 'light brown'],
      'light beige': ['light-beige', 'light beige', 'beige'],
      floral: ['floral'],
      'rainbow color': ['rainbow'],
      mint: ['mint'],
      'electric blue': ['electric-blue', 'electric blue'],
      indigo: ['indigo'],
      'olive green': ['olive-green', 'olive green', 'olive'],
      'peacock blue': ['peacock-blue', 'peacock blue', 'peacock'],
      teal: ['teal'],
      burgundy: ['burgundy'],
      'light gray': ['light-gray', 'light gray'],
      'black+pattern': ['black', 'pattern'],
      'gray+pattern': ['gray', 'grey', 'pattern'],
      'red+pattern': ['red', 'pattern'],
      'yellow+pattern': ['yellow', 'pattern'],
      'blue+pattern': ['blue', 'pattern'],
      'pink+pattern': ['pink', 'pattern'],
      'black pattern': ['black', 'pattern'],
      'yellow pattern': ['yellow', 'pattern'],
      'blue pattern': ['blue', 'pattern'],
      'pink pattern': ['pink', 'pattern'],
    };

    return colorTerms[normalizedColor] || [normalizedColor.replace(/\s+/g, '-'), normalizedColor].filter(Boolean);
  }

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
